import React, { useState, useEffect } from "react";
import styles from "./css/MyAccount.module.css";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import DownloadIcon from "@mui/icons-material/Download";
import axios from "axios";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Accordion, AccordionSummary, AccordionDetails } from "@mui/material";
import { useSelector } from "react-redux";

const Orders = () => {
  const [orders, setOrders] = useState([]);
  const language = useSelector((state: any) => state.language.value);
  const accessToken = localStorage.getItem("accessToken");

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        if (!accessToken) {
          console.error("No access token found");
          return;
        }

        // Debug: Check token format
        console.log("Token format check:", {
          tokenExists: !!accessToken,
          tokenLength: accessToken.length,
          tokenStart: accessToken.substring(0, 20) + "...",
        });

        const response = await axios.get(
          `${process.env.REACT_APP_SERVER_URL}/purchases/my-purchases`,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
              "Content-Type": "application/json",
            },
          }
        );

        console.log("Orders response:", response.data);
        setOrders(response.data);
      } catch (error: any) {
        // Enhanced error logging
        console.error("Full error object:", error);
        console.error("Response status:", error.response?.status);
        console.error("Response data:", error.response?.data);
        console.error("Request config:", error.config);

        if (error.response?.status === 401) {
          console.error(
            "Authentication failed. Token might be invalid or expired"
          );
          localStorage.removeItem("accessToken"); // Clear invalid token
        }
      }
    };

    if (accessToken) {
      fetchOrders();
    }
  }, [language, accessToken]);

  const downloadBook = async (format: string) => {
    if (!format) {
      console.error("No format provided for download");
      return;
    }

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_SERVER_URL}/purchases/download/${format}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          responseType: "blob",
        }
      );

      const fileName = format === "audio" ? "sample-audio.mp3" : "sample-1.pdf";
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      console.log("Download successful");
    } catch (error) {
      console.error("Error during file download:", error);
      notify(false);
    }
  };

  const renderActionButton = (order: any) => {
    const format = order?.format?.toUpperCase();
    const bookTitle = order?.book?.title || "book";

    const handleClick = (e: React.MouseEvent) => {
      e.stopPropagation();
    };

    if (format === "PHYSICAL") {
      return (
        <TableCell width="20%" style={{ border: "none" }} align="right">
          <div
            className={styles.actionButton}
            style={{ visibility: "hidden", height: "40px" }}
          >
            <span>{language === "RO" ? "Descarcă" : "Download"}</span>
          </div>
        </TableCell>
      );
    }

    if (format === "AUDIO") {
      return (
        <TableCell width="20%" style={{ border: "none" }} align="right">
          <a
            href="#/admin/audio_player"
            className={styles.actionButton}
            onClick={handleClick}
          >
            <span>{language === "RO" ? "Ascultă" : "Listen"}</span>
          </a>
        </TableCell>
      );
    }

    // For PDF or other downloadable formats
    return (
      <TableCell width="20%" style={{ border: "none" }} align="right">
        <div
          className={styles.actionButton}
          onClick={(e) => {
            e.stopPropagation();
            downloadBook("pdf");
          }}
        >
          <DownloadIcon />
          <span>{language === "RO" ? "Descarcă" : "Download"}</span>
        </div>
      </TableCell>
    );
  };

  return (
    <div className={styles.productContainer}>
      <span className={styles.productTitle}>
        {language === "RO" ? "Comenzile mele" : "Orders"}
      </span>
      <div className={styles.productInfo} style={{ flexDirection: "column" }}>
        <div className={styles.tableWrapper}>
          <TableContainer
            component={Paper}
            style={{
              background: "transparent",
              boxShadow: "none",
              borderRadius: "8px",
              overflow: "auto",
            }}
          >
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell
                    className={styles.tableHeader}
                    style={{ minWidth: "50px" }}
                  >
                    #
                  </TableCell>
                  <TableCell
                    className={styles.tableHeader}
                    style={{ minWidth: "200px" }}
                  >
                    {language === "RO" ? "Titlul cărții" : "Book Title"}
                  </TableCell>
                  <TableCell
                    className={styles.tableHeader}
                    style={{ minWidth: "100px" }}
                  >
                    {language === "RO" ? "Format" : "Format"}
                  </TableCell>
                  <TableCell
                    className={styles.tableHeader}
                    align="center"
                    style={{ minWidth: "100px" }}
                  >
                    {language === "RO" ? "Cantitate" : "Quantity"}
                  </TableCell>
                  <TableCell
                    className={styles.tableHeader}
                    align="center"
                    style={{ minWidth: "120px" }}
                  >
                    {language === "RO" ? "Data" : "Date"}
                  </TableCell>
                  <TableCell
                    className={styles.tableHeader}
                    align="right"
                    style={{ minWidth: "120px" }}
                  >
                    {language === "RO" ? "Preț total" : "Total Price"}
                  </TableCell>
                  <TableCell
                    className={styles.tableHeader}
                    align="right"
                    style={{ minWidth: "150px" }}
                  >
                    {language === "RO" ? "Acțiuni" : "Actions"}
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {orders.map((order: any, index) => (
                  <TableRow key={order?._id} className={styles.tableRow}>
                    <TableCell
                      colSpan={8}
                      style={{ padding: 0, border: "none" }}
                    >
                      <Accordion
                        style={{
                          width: "100%",
                          background: "transparent",
                          marginBottom: "8px",
                        }}
                        sx={{
                          "&.MuiPaper-root": {
                            border: "none",
                          },
                          "&:before": {
                            display: "none",
                          },
                          "& .MuiAccordionSummary-root": {
                            border: "none",
                          },
                        }}
                      >
                        <AccordionSummary
                          expandIcon={<ExpandMoreIcon />}
                          className={styles.accordionSummary}
                        >
                          <Table style={{ background: "transparent" }}>
                            <TableBody>
                              <TableRow>
                                <TableCell
                                  style={{ border: "none" }}
                                  width="5%"
                                >
                                  {index + 1}
                                </TableCell>
                                <TableCell
                                  style={{ border: "none" }}
                                  width="25%"
                                >
                                  {order?.book?.title}
                                </TableCell>
                                <TableCell
                                  style={{ border: "none" }}
                                  width="10%"
                                >
                                  <span className={styles.formatBadge}>
                                    {order?.format}
                                  </span>
                                </TableCell>
                                <TableCell
                                  style={{ border: "none" }}
                                  width="10%"
                                  align="center"
                                >
                                  {order?.quantity}
                                </TableCell>
                                <TableCell
                                  style={{ border: "none" }}
                                  width="15%"
                                  align="center"
                                >
                                  {new Date(
                                    order?.createdAt
                                  ).toLocaleDateString()}
                                </TableCell>
                                <TableCell
                                  style={{ border: "none" }}
                                  width="15%"
                                  align="right"
                                >
                                  {order?.quantity * order?.book?.price} RON
                                </TableCell>
                                {renderActionButton(order)}
                              </TableRow>
                            </TableBody>
                          </Table>
                        </AccordionSummary>
                        <AccordionDetails
                          style={{
                            border: "1px solid #C4B57C",
                            background: "#FBF8EB",
                            borderRadius: "0 0 4px 4px",
                            padding: "16px",
                          }}
                        >
                          <p>
                            <strong>
                              {language === "RO" ? "ID Comandă:" : "Order ID:"}
                            </strong>{" "}
                            {order._id}
                          </p>
                        </AccordionDetails>
                      </Accordion>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </div>
      </div>
    </div>
  );
};

export default Orders;
